'use client'

import {
	forwardRef,
	ReactNode,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react'
import SimpleBar from 'simplebar-react'

export type TSmartScrollContainerHandle = { scrollToEnd: () => void }

const DEFAULT_DRAG_DIRECTION = 'horizontal'

export const SmartScrollContainer = forwardRef<
	TSmartScrollContainerHandle,
	{
		children: ReactNode
		className?: string
		scrollbarPaddingHeight?: number
		direction?: 'horizontal' | 'vertical'
		maxHeight?: number
		// Opt-in, like RhcField's fieldName — this package stays analytics-
		// agnostic; fires once per mount on the first real scroll.
		onFirstScroll?: () => void
	}
>(
	(
		{
			children,
			className,
			scrollbarPaddingHeight = 20,
			direction = DEFAULT_DRAG_DIRECTION,
			maxHeight,
			onFirstScroll,
		},
		ref,
	) => {
		const [hydrated, setHydrated] = useState<boolean>(false)

		const wrapperRef = useRef<HTMLDivElement>(null)

		// Custom hook for drag scrolling
		const useDragScroll = () => {
			const [isDragging, setIsDragging] = useState(false)
			const dragState = useRef<{
				isDown: boolean // Is the pointer currently down?
				startX: number // Initial X position on pointerdown
				startY: number // Initial Y position on pointerdown
				scrollLeft: number // Initial scrollLeft on pointerdown
				scrollTop: number // Initial scrollTop on pointerdown
				isPotentialDrag: boolean // Has the pointer moved enough to be considered a potential drag?
				dragActive: boolean // Is an active drag operation in progress?
				pointerId: number | null // Stores the pointer ID for capture
				direction: 'horizontal' | 'vertical' // Current drag direction
			}>({
				isDown: false,
				startX: 0,
				startY: 0,
				scrollLeft: 0,
				scrollTop: 0,
				isPotentialDrag: false,
				dragActive: false,
				pointerId: null,
				direction,
			})

			// Helper to get the scrollable element (SimpleBar's content wrapper)
			const getScrollElement = useCallback(
				() =>
					wrapperRef.current?.querySelector(
						'.simplebar-content-wrapper',
					) as HTMLElement | null,
				[],
			)

			// Threshold for detecting a drag vs. a click
			const DRAG_THRESHOLD = 5 // Pixels

			const handlePointerDown = (e: React.PointerEvent) => {
				const scrollElement = getScrollElement()
				if (!scrollElement) {
					return
				}

				// Only handle left mouse button (primary pointer)
				if (e.button !== 0) {
					return
				}

				dragState.current.isDown = true
				dragState.current.startX = e.pageX - scrollElement.offsetLeft
				dragState.current.startY = e.pageY - scrollElement.offsetTop
				dragState.current.scrollLeft = scrollElement.scrollLeft
				dragState.current.scrollTop = scrollElement.scrollTop
				dragState.current.isPotentialDrag = false // Reset potential drag flag
				dragState.current.dragActive = false // Reset active drag flag
				dragState.current.pointerId = e.pointerId
				dragState.current.direction = direction

				// Important: Do NOT preventDefault here. This allows native click events to fire.
				// Pointer capture will be set only if a drag is confirmed.
			}

			const handleResetDragState = () => {
				// Reset all drag state variables
				setIsDragging(false)
				dragState.current.isDown = false
				dragState.current.startX = 0
				dragState.current.startY = 0
				dragState.current.scrollLeft = 0
				dragState.current.scrollTop = 0
				dragState.current.isPotentialDrag = false
				dragState.current.dragActive = false
				dragState.current.pointerId = null
				dragState.current.direction = direction
			}

			const handlePointerMove = (e: React.PointerEvent) => {
				// Only process if the pointer is down and it's the tracked pointer
				if (
					!dragState.current.isDown ||
					e.pointerId !== dragState.current.pointerId
				)
					return

				const scrollElement = getScrollElement()
				if (!scrollElement) return

				const x = e.pageX - scrollElement.offsetLeft
				const y = e.pageY - scrollElement.offsetTop
				const distanceX = Math.abs(x - dragState.current.startX)
				const distanceY = Math.abs(y - dragState.current.startY)

				if (!dragState.current.dragActive) {
					// If not yet actively dragging, check if threshold is met
					if (
						distanceX > DRAG_THRESHOLD ||
						distanceY > DRAG_THRESHOLD
					) {
						dragState.current.isPotentialDrag = true
						// If it's a potential drag, activate drag and set pointer capture
						// This prevents text selection and other native behaviors during drag
						setIsDragging(true)
						dragState.current.dragActive = true
						scrollElement.setPointerCapture(e.pointerId)
						e.preventDefault()

						// Determine drag direction
						dragState.current.direction =
							distanceX > distanceY ? 'horizontal' : 'vertical'
					}
				}

				// If drag is active, perform scrolling
				if (dragState.current.dragActive) {
					// Prevent default even if movement is small, once drag is active
					e.preventDefault()
					if (dragState.current.direction === 'horizontal') {
						const walkX = x - dragState.current.startX
						scrollElement.scrollLeft =
							dragState.current.scrollLeft - walkX
					} else if (dragState.current.direction === 'vertical') {
						const walkY = y - dragState.current.startY
						scrollElement.scrollTop =
							dragState.current.scrollTop - walkY
					}
				}
			}

			const handlePointerUp = (e: React.PointerEvent) => {
				// Only process if the pointer is down and it's the tracked pointer
				if (
					!dragState.current.isDown ||
					e.pointerId !== dragState.current.pointerId
				)
					return

				const scrollElement = getScrollElement()

				// Release pointer capture if it was set

				if (
					scrollElement &&
					scrollElement.hasPointerCapture(e.pointerId)
				) {
					scrollElement.releasePointerCapture(e.pointerId)
				}

				// If a drag was active, prevent the default behavior (e.g., click)
				// This is crucial: only prevent default if an actual drag occurred.
				if (dragState.current.dragActive) {
					e.preventDefault()
				}

				handleResetDragState()
			}

			// handlePointerCancel is important for when the OS takes over (e.g., cmd+tab, alt+tab)
			const handlePointerCancel = (e: React.PointerEvent) => {
				const scrollElement = getScrollElement()
				if (
					scrollElement &&
					scrollElement.hasPointerCapture(e.pointerId)
				) {
					scrollElement.releasePointerCapture(e.pointerId)
				}
				handleResetDragState()
			}

			const handlePointerLeave = (e: React.PointerEvent) => {
				// If pointer leaves the element while dragging, treat it like a cancel
				// to avoid stuck drag state
				if (
					dragState.current.dragActive ||
					dragState.current.isPotentialDrag
				) {
					handlePointerCancel(e)
				}
			}

			return {
				isDragging,
				dragHandlers: {
					onPointerDown: handlePointerDown,
					onPointerMove: handlePointerMove,
					onPointerUp: handlePointerUp,
					onPointerCancel: handlePointerCancel,
					onPointerLeave: handlePointerLeave,
				},
			}
		}

		const { dragHandlers } = useDragScroll()

		useImperativeHandle(
			ref,
			() => ({
				scrollToEnd: () => {
					const scrollWrapper = wrapperRef.current?.querySelector(
						'.simplebar-content-wrapper',
					) as HTMLElement | null

					if (scrollWrapper) {
						scrollWrapper.scrollTo({
							left:
								scrollWrapper.scrollWidth -
								scrollWrapper.clientWidth,
							top:
								scrollWrapper.scrollHeight -
								scrollWrapper.clientHeight,
							behavior: 'smooth',
						})
					}
				},
			}),
			[],
		)

		useEffect(() => {
			setHydrated(true)
		}, [])

		useEffect(() => {
			if (!hydrated || !wrapperRef.current) return

			const scrollWrapper = wrapperRef.current.querySelector(
				'.simplebar-content-wrapper',
			) as HTMLElement | null
			const scrollContent = wrapperRef.current.querySelector(
				'.simplebar-content',
			) as HTMLElement | null

			if (!scrollWrapper || !scrollContent) return

			let hasFiredFirstScroll = false
			const handleScroll = () => {
				if (hasFiredFirstScroll) return
				hasFiredFirstScroll = true
				onFirstScroll?.()
			}
			if (onFirstScroll) {
				scrollWrapper.addEventListener('scroll', handleScroll, {
					passive: true,
				})
			}

			let animationFrameId: number | null = null
			// Track previous scrollability state to prevent unnecessary updates
			let previousHasScroll: boolean | null = null // null means uninitialized
			let isUpdating = false // Guard to prevent re-entrant updates

			const updateShimAndScrollability = () => {
				if (isUpdating) return // Prevent re-entrant calls
				if (animationFrameId) cancelAnimationFrame(animationFrameId)

				animationFrameId = requestAnimationFrame(() => {
					isUpdating = true
					try {
						const hasScrollX =
							scrollWrapper.scrollWidth >
							scrollWrapper.clientWidth
						const hasScrollY =
							scrollWrapper.scrollHeight >
							scrollWrapper.clientHeight
						const hasScroll = hasScrollX || hasScrollY

						// Only update if scrollability state actually changed (or if uninitialized)
						if (
							previousHasScroll !== null &&
							hasScroll === previousHasScroll
						) {
							isUpdating = false
							return
						}

						previousHasScroll = hasScroll
						const existingShim = scrollContent.querySelector(
							'[data-scrollbar-shim]',
						)

						if (hasScroll && !existingShim) {
							const shim = document.createElement('div')
							shim.dataset.scrollbarShim = 'true'
							shim.style.height = `${scrollbarPaddingHeight}px`
							shim.style.width = '100%'
							shim.style.pointerEvents = 'none'
							scrollContent.appendChild(shim)
						} else if (!hasScroll && existingShim) {
							existingShim.remove()
						}
					} finally {
						// Reset the guard after a short delay to allow DOM to settle
						setTimeout(() => {
							isUpdating = false
						}, 0)
					}
				})
			}

			// Initial call to set up the shim and initialize state
			updateShimAndScrollability()

			const resizeObserver = new ResizeObserver(
				updateShimAndScrollability,
			)
			resizeObserver.observe(scrollWrapper)
			resizeObserver.observe(scrollContent)

			// Filter mutations to ignore changes from the shim itself
			const mutationObserver = new MutationObserver((mutations) => {
				// Skip mutations that are only about the shim being added/removed
				const hasNonShimMutations = mutations.some((mutation) => {
					// Check if any added/removed node is NOT the shim
					for (const node of Array.from(mutation.addedNodes)) {
						if (
							node.nodeType === Node.ELEMENT_NODE &&
							!(node as Element).hasAttribute(
								'data-scrollbar-shim',
							)
						) {
							return true
						}
					}
					for (const node of Array.from(mutation.removedNodes)) {
						if (
							node.nodeType === Node.ELEMENT_NODE &&
							!(node as Element).hasAttribute(
								'data-scrollbar-shim',
							)
						) {
							return true
						}
					}
					return false
				})

				// Only update if there are non-shim mutations
				if (hasNonShimMutations) {
					updateShimAndScrollability()
				}
			})
			mutationObserver.observe(scrollContent, {
				childList: true,
				subtree: true, // also catch updates inside children
			})

			return () => {
				resizeObserver.disconnect()
				mutationObserver.disconnect()
				if (animationFrameId) cancelAnimationFrame(animationFrameId)
				if (onFirstScroll) {
					scrollWrapper.removeEventListener('scroll', handleScroll)
				}
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [hydrated, scrollbarPaddingHeight])

		if (!hydrated) {
			return (
				<div
					ref={wrapperRef}
					style={{
						overflowX: 'auto',
						overflowY: 'auto',
						whiteSpace: 'nowrap',
					}}
					className={className}
				>
					{children}
				</div>
			)
		}

		return (
			<div ref={wrapperRef} className={className}>
				<SimpleBar
					autoHide={false}
					scrollbarMinSize={32}
					style={{
						maxHeight: maxHeight && `${maxHeight}px`,
						overflowX:
							direction === 'horizontal' ? 'auto' : 'hidden',
						overflowY: direction === 'vertical' ? 'auto' : 'hidden',
						whiteSpace:
							direction === 'horizontal' ? 'nowrap' : 'normal',
						touchAction: 'pan-x pan-y', // Improves touch responsiveness on touch-enabled devices
					}}
					{...dragHandlers}
				>
					{children}
				</SimpleBar>
			</div>
		)
	},
)

SmartScrollContainer.displayName = 'SmartScrollContainer'
